import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { getCurrencyListRequest } from 'redux/reducers/currency/reducer';
import { getCurrencyList, getCurrencyListLoading } from 'redux/reducers/currency/selectors';
import { TCurrencyDetails } from 'redux/reducers/currency/types';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { CurrencyDialog } from './CurrencyDialog';

const EDIT_ACTION = 'Edit';

export const Currencies: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [currencyToEdit, setCurrencyToEdit] = useState<TCurrencyDetails | null>(null);

	const list = useSelector(getCurrencyList);
	const listLoading = useSelector(getCurrencyListLoading);

	/** Keeps only the visibility flags, so the columns stay free of the derived render callbacks. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TCurrencyDetails>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.ASC });

		dispatch(
			getCurrencyListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				setCurrencyToEdit(list?.data?.find((item) => item.id === id) ?? null);
				setDialogOpen(true);
			}
		},
		[list],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setCurrencyToEdit(null);
	}, []);

	return (
		<>
			<CurrencyDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				currencyToEdit={currencyToEdit}
			/>
			<EasTableWithFilter<TCurrencyDetails>
				tableStructure={currentTableStructure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / General / <b>Currencies</b>
					</Typography>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
